import React, { useEffect, useState, Fragment } from "react";
import { useSelector } from "react-redux";
import { useAxiosInstance } from "../../../services/Interceptor";
import { getAllTimeBestResults } from "../../../services/AthleteService";
import { getAllTimeBestResultsByEvent } from "../../../services/CombineService";
import LoadingSpinner from "../../LoadingSpinner";
import ProgressBar from "../athlete-progress-bar";
import AthleteCombineListResults from "./athlete-combine-list-results";
import AthleteCombineListPosition from "./athlete-combine-list-position";
import RangeInfo from "../../RangeInfo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { useSubscriptionAccess } from "../../../hooks/useSubscriptionAccess";

function CombineResults({ profile = {} }) {
  const r_athlete = useSelector((state) => state.athletes);
  const [result, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiInstance = useAxiosInstance();
  const [athleticScore, setAthleticScore] = useState(0);
  const [positionScore, setPositionScore] = useState(0);
  const [positionYearScore, setPositionYearScore] = useState(0);
  const { hasSubscriptionAccess } = useSubscriptionAccess();
  const {
    buttons: {
      combine: {
        tab,
        athleteTabSelected,
        positionTabSelected,
        positionYearTabSelected,
      },
    },
  } = r_athlete;

  const fetchResults = async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    if (r_athlete.config.searchEvent && !r_athlete.config.eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let results = [];
      if (
        r_athlete.config.searchEvent &&
        r_athlete.config.eventId !== undefined
      ) {
        results = await getAllTimeBestResultsByEvent(
          apiInstance,
          profile?.id,
          r_athlete.config.eventId,
        );
      } else {
        results = await getAllTimeBestResults(apiInstance, profile?.id);
      }
      setResults(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!profile?.id) return;
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, r_athlete.config.eventId]);

  useEffect(() => {
    if (result.length > 0) {
      setAthleticScore(Math.round(r_athlete.profile.overallAthleticScore));
      setPositionScore(
        Math.round(r_athlete.profile.overallPositionAthleticScore),
      );
      setPositionYearScore(
        Math.round(
          r_athlete.profile.overallPositionYearAthleticScore ??
            r_athlete.profile.positionYearScore ??
            r_athlete.profile.overallPositionAthleticScore ??
            0,
        ),
      );
    }
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  if (r_athlete.config.searchEvent && !r_athlete.config.eventId) {
    return (
      <div className="text-center my-10 text-gray-600">
        {" "}
        Select an event to see the results.{" "}
      </div>
    );
  }
  if (loading)
    return (
      <div className="my-10">
        {" "}
        <LoadingSpinner />{" "}
      </div>
    );

  const kickerChartingResults = result.filter(
    (r) => r.physicalTestName === "Kicker Charting",
  );

  const fortyYardDashOrdered = result
    .filter((r) => r.physicalTestName === "40 Yard dash")
    .sort((a, b) => {
      const order = { 10: 1, 20: 2, 40: 3 };
      return order[a.stage] - order[b.stage];
    });

  const otherResults = result.filter(
    (r) =>
      r.physicalTestName !== "40 Yard dash" &&
      r.physicalTestName !== "Kicker Charting",
  );

  const orderedResults = [
    ...kickerChartingResults,
    ...fortyYardDashOrdered,
    ...otherResults,
  ];

  return (
    <>
      <div className="-mt-6 px-4 -m-4 relative h-auto">
        {!hasSubscriptionAccess && (
          <div className="absolute top-9 right-4 z-10 bg-hide w-[35%] lg:w-[50%] h-[95%] rounded-r-md mr-0">
            <div className="text-center h-full px-6 py-36">
              <FontAwesomeIcon icon={faLock} className="h-6 pt-1" />
              <p className="text-sm">
                Unlock premium features and exclusive content with a full
                subscription.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <RangeInfo />
        </div>

        {tab && athleteTabSelected && (
          <div>
            <div className="mt-0">
              <div className="mb-2  bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 items-center">
                <div className=" flex font-medium ">Trackit Athletic Score</div>
                {hasSubscriptionAccess && (
                  <div className="w-full ">
                    <ProgressBar score={athleticScore} />
                  </div>
                )}
              </div>
              {orderedResults?.map((result, key) => (
                <Fragment key={key}>
                  <AthleteCombineListResults
                    key={key}
                    result={result}
                    athleteId={profile?.id}
                  />
                </Fragment>
              ))}
            </div>
          </div>
        )}
        {tab && positionTabSelected && (
          <div>
            <div className="mt-0">
              <div className="mb-2  bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 items-center">
                <div className=" flex font-medium ">
                  Position Athletic Score
                </div>
                {hasSubscriptionAccess && (
                  <div className="w-full ">
                    <ProgressBar score={positionScore} />
                  </div>
                )}
              </div>
              {orderedResults?.map((result, key) => (
                <AthleteCombineListPosition
                  key={key}
                  result={result}
                  athleteId={profile?.id}
                />
              ))}
            </div>
          </div>
        )}
        {tab && positionYearTabSelected && (
          <div>
            <div className="mt-0">
              <div className="mb-2  bg-blue-50 rounded-lg border border-blue-200 px-3 py-2 items-center">
                <div className=" flex font-medium ">
                  Position/Age Athletic Score
                </div>
                {hasSubscriptionAccess && (
                  <div className="w-full ">
                    <ProgressBar score={positionYearScore} />
                  </div>
                )}
              </div>
              {orderedResults?.map((result, key) => (
                <AthleteCombineListPosition
                  key={key}
                  result={result}
                  athleteId={r_athlete.profile.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
export default CombineResults;
